import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'
import type { Category } from '~/types'

export const useCategoriesStore = defineStore('categories', () => {
  const { $axios, $i18n } = useNuxtApp()
  const categories = ref<Category[]>([])
  const categoriesLanguage = ref<string>('')

  const lastFetched = useStorage<number>('categories-last-fetched', 0)

  const route = useRoute()
  const restaurantStore = useRestaurantStore()
  const restaurantViewModeStore = useRestaurantViewModeStore()
  const isLoadingCategories = ref<boolean>(false)

  const fetchCategories = async (forceRefresh = false) => {
    const now = Date.now()
    const cacheValid = now - lastFetched.value < 1000 * 60 * 60
    const langValid = categoriesLanguage.value === $i18n.locale.value

    if (!forceRefresh && categories.value.length > 0 && cacheValid && langValid) {
      return categories.value
    }

    isLoadingCategories.value = true

    let slug = useSafeStorage('_slug') || restaurantStore.slug
    let qr_code = useSafeStorage('_id')
    let menu_ids = useSafeStorage('_menu_ids')
    let table_id = useSafeStorage('_table_id')
    let branch_id = useSafeStorage('_branch_id') || restaurantStore.branchId

    const parseMenuIds = (rawMenuIds: string | null | undefined) => {
      if (!rawMenuIds) return []

      try {
        const parsed = JSON.parse(rawMenuIds)
        return Array.isArray(parsed) ? parsed.map(String) : []
      } catch {
        return []
      }
    }

    if (!restaurantStore.slug && slug) {
      restaurantStore.slug = slug
    }

    const storedActiveMenuId = useSafeStorage('_active_menu_id')
    if (!restaurantStore.activeMenuId && storedActiveMenuId) {
      restaurantStore.activeMenuId = storedActiveMenuId
    }

    const storedMenuIds = parseMenuIds(menu_ids)
    if (!restaurantStore.menuIds.length && storedMenuIds.length) {
      restaurantStore.menuIds = storedMenuIds
    }

    if (!restaurantStore.activeMenuId && restaurantStore.menuIds.length === 1) {
      restaurantStore.activeMenuId = String(restaurantStore.menuIds[0])
      useSetSafeStorage('_active_menu_id', restaurantStore.activeMenuId)
    }

    if (qr_code && !restaurantStore.menuIds.length) {
      let response: any = ''
      response = await restaurantStore.fetchQRcode(qr_code)
      if (response.data && response.data.menu_ids.length) {
        menu_ids = response.data.menu_ids
        table_id = response.data.table_id || ''
        branch_id = response.data.branch_id
          || (response.data.eligible_branches_count === 1 ? response.data.single_eligible_branch_id : restaurantStore.branchId)

        restaurantStore.setRestaurant(slug, qr_code, menu_ids, table_id, branch_id)
        restaurantViewModeStore.setModeFromApi(response.data.order_mode)
        restaurantStore.syncViewModeWithAvailability()

        if (!restaurantStore.activeMenuId && response.data.menu_ids.length === 1) {
          restaurantStore.activeMenuId = String(response.data.menu_ids[0])
          useSetSafeStorage('_active_menu_id', restaurantStore.activeMenuId)
        }
      }
    }
    try {
      let response: any = ''
      if (restaurantStore.activeMenuId) {
        response = await $axios.get(
          `/restaurant/${slug}/menus/${restaurantStore.activeMenuId}/categories/${$i18n.locale.value}`
        )
      }

      categories.value = response.data.data
      categoriesLanguage.value = $i18n.locale.value
      lastFetched.value = now
      return response.data.data
    } catch (error) {
      return categories.value
    } finally {
      isLoadingCategories.value = false
    }
  }

  const searchParam = ref<any>(null)

  return {
    categories,
    isLoadingCategories,
    searchParam,

    fetchCategories,
  }
})
